import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { listSubjectsCached } from '../../api/subjectPortalApi';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectBox = styled.div`
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;

  &:focus-within {
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }
`;

const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$isChild ? '#e0f2fe' : '#fef3e2'};
  border: 1px solid ${props => props.$isChild ? '#0284c7' : '#F1AF59'};
  border-radius: 4px;
  font-size: 13px;
  color: ${props => props.$isChild ? '#0369a1' : '#92400e'};

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    line-height: 1;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const Placeholder = styled.span`
  color: #9ca3af;
  font-size: 14px;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  margin-top: 4px;
`;

const ParentOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.$selected ? '#fef3e2' : 'white'};
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    background: ${props => props.$selected ? '#fef3e2' : '#f3f4f6'};
  }
`;

const ChildOption = styled.div`
  padding: 8px 12px 8px 36px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.$selected ? '#e0f2fe' : '#fafafa'};

  &:hover {
    background: ${props => props.$selected ? '#e0f2fe' : '#f3f4f6'};
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #F1AF59;
  cursor: pointer;
`;

const NoResults = styled.div`
  padding: 12px;
  color: #9ca3af;
  font-size: 14px;
  text-align: center;
`;

const SubjectMultiSelect = ({
  value = [],
  onChange,
  placeholder = 'Select subjects...',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  // Load subjects on mount
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const data = await listSubjectsCached();
        setSubjects(data);
      } catch (error) {
        console.error('Failed to load subjects:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSubjects();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if a parent subject is selected
  const isParentSelected = (parentId) => {
    return value.some(s => s.subjectId === parentId);
  };

  // Check if a child subject is selected
  const isChildSelected = (parentId, childId) => {
    const parent = value.find(s => s.subjectId === parentId);
    if (!parent) return false;
    return (parent.children || []).some(c => c.subjectId === childId);
  };

  // Toggle parent subject selection
  const toggleParent = (parent) => {
    if (disabled) return;

    const isSelected = isParentSelected(parent.id);
    if (isSelected) {
      // Remove parent and all its children
      onChange(value.filter(s => s.subjectId !== parent.id));
    } else {
      // Add parent with empty children array
      onChange([...value, {
        subjectId: parent.id,
        subjectName: parent.subjectName,
        children: []
      }]);
    }
  };

  // Toggle child subject selection
  const toggleChild = (parent, child) => {
    if (disabled) return;

    const parentIndex = value.findIndex(s => s.subjectId === parent.id);

    if (parentIndex === -1) {
      // Parent not selected, add parent with this child
      onChange([...value, {
        subjectId: parent.id,
        subjectName: parent.subjectName,
        children: [{
          subjectId: child.id,
          subjectName: child.subjectName
        }]
      }]);
    } else {
      // Parent already selected, toggle child
      const updatedValue = [...value];
      const parentSubject = { ...updatedValue[parentIndex] };
      const children = parentSubject.children || [];
      const childIndex = children.findIndex(c => c.subjectId === child.id);

      if (childIndex === -1) {
        // Add child
        parentSubject.children = [...children, {
          subjectId: child.id,
          subjectName: child.subjectName
        }];
      } else {
        // Remove child
        parentSubject.children = children.filter(c => c.subjectId !== child.id);
      }

      updatedValue[parentIndex] = parentSubject;
      onChange(updatedValue);
    }
  };

  // Remove a selected item
  const removeSelected = (e, parentId, childId = null) => {
    e.stopPropagation();
    if (disabled) return;

    if (childId) {
      // Remove specific child
      const updatedValue = value.map(s => {
        if (s.subjectId === parentId) {
          return {
            ...s,
            children: (s.children || []).filter(c => c.subjectId !== childId)
          };
        }
        return s;
      });
      onChange(updatedValue);
    } else {
      // Remove parent and all children
      onChange(value.filter(s => s.subjectId !== parentId));
    }
  };

  // Render selected tags
  const renderSelectedTags = () => {
    const tags = [];

    value.forEach(parent => {
      // Add parent tag
      tags.push(
        <SelectedTag key={parent.subjectId} $isChild={false}>
          {parent.subjectName}
          <button type="button" onClick={(e) => removeSelected(e, parent.subjectId)}>
            ×
          </button>
        </SelectedTag>
      );

      // Add child tags
      (parent.children || []).forEach(child => {
        tags.push(
          <SelectedTag key={`${parent.subjectId}-${child.subjectId}`} $isChild={true}>
            {child.subjectName}
            <button type="button" onClick={(e) => removeSelected(e, parent.subjectId, child.subjectId)}>
              ×
            </button>
          </SelectedTag>
        );
      });
    });

    return tags;
  };

  if (loading) {
    return (
      <SelectBox style={{ cursor: 'default' }}>
        <Placeholder>Loading subjects...</Placeholder>
      </SelectBox>
    );
  }

  return (
    <Container ref={containerRef}>
      <SelectBox onClick={() => !disabled && setIsOpen(!isOpen)}>
        {value.length === 0 ? (
          <Placeholder>{placeholder}</Placeholder>
        ) : (
          renderSelectedTags()
        )}
      </SelectBox>

      {isOpen && !disabled && (
        <Dropdown>
          {subjects.length === 0 ? (
            <NoResults>No subjects available</NoResults>
          ) : (
            subjects.map(parent => (
              <React.Fragment key={parent.id}>
                <ParentOption
                  $selected={isParentSelected(parent.id)}
                  onClick={() => toggleParent(parent)}
                >
                  <Checkbox
                    type="checkbox"
                    checked={isParentSelected(parent.id)}
                    onChange={() => {}}
                    onClick={(e) => { e.stopPropagation(); toggleParent(parent); }}
                  />
                  {parent.subjectName}
                </ParentOption>

                {/* Show children if parent is selected */}
                {isParentSelected(parent.id) && parent.children && parent.children.length > 0 && (
                  parent.children.map(child => (
                    <ChildOption
                      key={child.id}
                      $selected={isChildSelected(parent.id, child.id)}
                      onClick={() => toggleChild(parent, child)}
                    >
                      <Checkbox
                        type="checkbox"
                        checked={isChildSelected(parent.id, child.id)}
                        onChange={() => {}}
                        onClick={(e) => { e.stopPropagation(); toggleChild(parent, child); }}
                      />
                      {child.subjectName}
                    </ChildOption>
                  ))
                )}
              </React.Fragment>
            ))
          )}
        </Dropdown>
      )}
    </Container>
  );
};

export default SubjectMultiSelect;
